import React, { memo, useEffect, useMemo, useState, useContext } from 'react';
import './incidents.css';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  Backdrop,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import IncidentsTable from '../../components/Incidents/IncidentsTable';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  getCallStatus,
  getDepartmentsList,
  getEmployeesList,
  getFormSettings,
  getIncidentInfo,
  getIncidentsList,
  syncNeris,
} from '../../store/incidentsReducer';
import useIncidentsColumns from './hooks/useIncidentsColumns';
import ViewIncidentModal from '../../components/Incidents/modals/ViewIncidentModal';
import EditIncidentModal from '../../components/Incidents/modals/EditIncidentModal';
import { debounce } from 'lodash';
import moment from 'moment';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
// import ImportXml from './ImportXml';
import { useTableSearch } from '../../hooks/tables/useTableSearch';
import {
  deleteFilterParamsFromUrl,
  getFilterParams,
} from '../../hooks/useFiltersUrlState';
import { useSearchParams } from 'react-router-dom';
import CreateNote from '../../components/Incidents/modals/CreateNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  initSettings,
  SCHEMA,
} from '../../components/Incidents/FilterIncidents/urlConfig';
import PropertyDamageModal from '../../components/Incidents/modals/PropertyDamageModal';
import IncidentPrintView from '../../components/Incidents/modals/IncidentPrintView';
import IncidentsListPrintView, {
  LIST_PRINT_BODY_CLASS,
} from '../../components/Incidents/modals/IncidentsListPrintView';
import { incidentsApi } from '../../functions/IncidentsApi';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import {
  PeriodSelector,
  StartDatePicker,
  EndDatePicker,
  pickerValuesMap,
  pickerVariants,
  setDateRangeInUrl,
  useDateRangeFilter,
  generateYearOptions,
  pickerLabels,
} from '../../components/DatePickers/DateRangeFilter';

const datePickerPresetOptions = [
  { value: pickerValuesMap.custom,    label: pickerLabels[pickerValuesMap.custom] },
  { value: pickerValuesMap.last7Days, label: pickerLabels[pickerValuesMap.last7Days] },
  { value: pickerValuesMap.last30Days,label: pickerLabels[pickerValuesMap.last30Days] },
  { value: pickerValuesMap.lastYear,  label: pickerLabels[pickerValuesMap.lastYear] },
  { value: pickerValuesMap.today,     label: pickerLabels[pickerValuesMap.today] },
  { value: pickerValuesMap.mtd,       label: pickerLabels[pickerValuesMap.mtd] },
  { value: pickerValuesMap.ytd,       label: pickerLabels[pickerValuesMap.ytd] },
  ...generateYearOptions(2),
];

const INCIDENT_TYPES = ['Fire', 'Medical', 'Rescue', 'Hazsit', 'Noemerg', 'Lawenforce', 'Other'];
const LOCAL_NERIS_STATUSES = ['New', 'In Progress', 'Finished'];
const USFA_NERIS_STATUSES = ['Submitted', 'Rejected', 'Complete', 'In Progress', 'Failed', 'N/A'];
const RESPONSE_TIMES = ['<5 Min', '5-10 Min', '10+ Min'];
const PRINT_PAGE_SIZE = 100;
const PRINT_MAX_ROWS = 2000;

const selectSx = {
  height: '40px',
  fontSize: '14px',
  minWidth: '130px',
  backgroundColor: '#fff',
};

const Incidents = () => {
  const [searchParams, setParams] = useSearchParams();
  const {
    searchQuery,
    paginationModel,
    setSearchQuery,
    setPaginationModel,
    localSearchState,
    setLocalSearchState,
  } = useTableSearch();

  const [viewIncident, setViewIncident] = useState(null);
  const [editIncident, setEditIncident] = useState(null);
  const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
  const [openPropertyDamageModal, setOpenPropertyDamageModal] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [listPrintRows, setListPrintRows] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const { department } = useContext(AuthContext);

  // Inline filter state (URL-driven via searchParams)
  const getParam = (key) => searchParams.get(key) || '';

  const dateRangeFilter = useDateRangeFilter({
    defaultValue: pickerValuesMap.last30Days,
  });

  let {
    incidentsList,
    loading,
    incidentsPagination,
    employeesList,
    callStatus,
    selectedIncidentInfo,
  } = useSelector((state) => state.incidentsReducer);

  const { permissions } = useSelector(
    (state) => state.nerisSettingsAuthoritiesReducer
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDepartmentsList());
    dispatch(getEmployeesList());
    dispatch(getCallStatus());
  }, [dispatch]);

  const requestForIncidents = () => {
    const params = getFilterParams(SCHEMA, initSettings);
    dispatch(
      getIncidentsList({
        page: paginationModel.page + 1,
        searchQuery: searchQuery,
        pageSize: paginationModel.pageSize,
        ...params,
      })
    );
  };

  useEffect(() => {
    requestForIncidents();
  }, [paginationModel.page, paginationModel.pageSize, searchQuery]);

  const debouncedHandlerSearch = useMemo(
    () =>
      debounce((query) => {
        setParams((prev) => {
          deleteFilterParamsFromUrl(prev, SCHEMA);
          return prev;
        });
        setSearchQuery(query);
      }, 1000),
    [paginationModel.pageSize]
  );

  const handleSearchChange = (e) => {
    setLocalSearchState(e.target.value);
    debouncedHandlerSearch(e.target.value);
  };

  const clearSearch = () => {
    setLocalSearchState('');
    debouncedHandlerSearch('');
  };

  const handleSelectFilter = (urlKey) => (e) => {
    const value = e.target.value;
    setParams((prev) => {
      if (value) {
        prev.set(urlKey, value);
      } else {
        prev.delete(urlKey);
      }
      prev.set('page', '0');
      return prev;
    });
  };

  const openIncidentInfo = (id, edit = false) => {
    if (edit) {
      dispatch(getFormSettings(id));
      setEditIncident(true);
    } else {
      setViewIncident(true);
    }
    dispatch(getIncidentInfo(id));
  };

  const createIncident = () => {
    dispatch(getFormSettings());
    setEditIncident(true);
  };

  const handlerSyncNeris = (id) => {
    dispatch(syncNeris(id)).then((resp) => {
      if (resp) {
        requestForIncidents();
      }
    });
  };

  const handlePrintIncident = (id) => {
    setPrintLoading(true);
    dispatch(getIncidentInfo(id)).then(() => {
      setTimeout(() => {
        setPrintLoading(false);
        window.print();
      }, 150);
    });
  };

  // Fetch every page matching the current filters, then print as a flat table
  const handlePrintList = async () => {
    setPrintLoading(true);
    try {
      const params = getFilterParams(SCHEMA, initSettings);
      const allRows = [];
      let page = 1;
      let total = Infinity;
      while (allRows.length < total && allRows.length < PRINT_MAX_ROWS) {
        const response = await incidentsApi.getIncidents({
          ...params,
          page,
          searchQuery,
          pageSize: PRINT_PAGE_SIZE,
        });
        if (response.status !== 200) throw response.response?.data || response;
        const data = response.data.data || [];
        allRows.push(...data);
        total = response.data.pagination?.total ?? allRows.length;
        if (data.length < PRINT_PAGE_SIZE) break;
        page += 1;
      }
      if (allRows.length === 0) {
        toast.info('No incidents to print for the selected filters.');
        return;
      }
      if (allRows.length >= PRINT_MAX_ROWS && total > PRINT_MAX_ROWS) {
        toast.warn(`Printing the first ${PRINT_MAX_ROWS} of ${total} incidents. Narrow the filters to print the rest.`);
      }
      setListPrintRows(allRows);
      document.body.classList.add(LIST_PRINT_BODY_CLASS);
      const cleanup = () => {
        document.body.classList.remove(LIST_PRINT_BODY_CLASS);
        setListPrintRows(null);
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);
      setTimeout(() => window.print(), 150);
    } catch (err) {
      toast.error(err?.message || 'Failed to load incidents for printing');
      console.error(err);
    } finally {
      setPrintLoading(false);
    }
  };

  const printFiltersSummary = useMemo(() => {
    if (!listPrintRows) return [];
    const summary = [];
    const from = getParam('fromDate');
    const to = getParam('toDate');
    if (from || to) {
      summary.push({
        label: 'Date range',
        value: `${from ? moment(from).format('MM/DD/YYYY') : '…'} – ${to ? moment(to).format('MM/DD/YYYY') : '…'}`,
      });
    }
    const employeeIds = getParam('employee') ? getParam('employee').split(',') : [];
    if (employeeIds.length) {
      summary.push({
        label: 'Member',
        value: employeeIds
          .map((id) => employeesList?.find((e) => String(e.id) === String(id))?.label || id)
          .join(', '),
      });
    }
    if (searchQuery) summary.push({ label: 'Search', value: searchQuery });
    [
      ['type', 'Type'],
      ['status', 'Call status'],
      ['form_status', 'Local'],
      ['nerisStatus', 'USFA'],
    ].forEach(([key, label]) => {
      if (getParam(key)) summary.push({ label, value: getParam(key) });
    });
    return summary;
  }, [listPrintRows]);

  const columns = useIncidentsColumns({
    openIncidentInfo,
    handlerSyncNeris,
    setOpenCreateNoteModal,
		handlePrintIncident,
    permissions,
    employeesList,
    requestForIncidents,
    callStatus,
    setOpenPropertyDamageModal,
  });

  const handleApplyFilter = () => {
    requestForIncidents();
  };

  const filterMyReports = () => {
    setParams((prev) => {
      prev.set('employee', permissions.id);
      return prev;
    });
    // Use setTimeout so URL params are committed before the API call
    setTimeout(() => requestForIncidents(), 0);
  };
  
  const rows = useMemo(
    () =>
      incidentsList.map((i) => ({
        ...i,
        id: i.incident_id,
        neris_status: i.neris_status ? i.neris_status : 'N/A',
      })),
    [incidentsList]
  );

  const onPaginationModelChange = (e) => {
    setPaginationModel(e);
  };

  // Build call status options for Status dropdown
  const callStatusOptions = useMemo(
    () => callStatus?.map((item) => ({ value: item.label, label: item.label })) || [],
    [callStatus]
  );

  return (
    <>
      <Box>
        <Card
          className="p-3 mt-4"
          sx={{ boxShadow: '0px 4px 4px 0px #0000000F' }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Incidents
            </Typography>
            <Box className="d-flex" sx={{ gap: 1.5, alignItems: 'center' }}>
              {/* Search */}
              <TextField
                color="secondary"
                placeholder="Search incidents..."
                size="small"
                value={localSearchState}
                onChange={handleSearchChange}
                sx={{ width: '240px', backgroundColor: '#fff' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchQuery.length > 0 ? (
                        <ClearIcon onClick={clearSearch} sx={{ cursor: 'pointer' }} />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilter}
                sx={{
                  backgroundColor: '#BF1D2D',
                  '&:hover': { backgroundColor: '#a01825' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Apply Filter
              </Button>
              <Button
                variant="outlined"
                onClick={filterMyReports}
                sx={{
                  borderColor: '#BF1D2D',
                  color: '#BF1D2D',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#a01825', backgroundColor: '#fef2f2' },
                }}
              >
                My Reports
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintList}
                disabled={printLoading}
                sx={{
                  borderColor: '#BF1D2D',
                  color: '#BF1D2D',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#a01825', backgroundColor: '#fef2f2' },
                }}
              >
                Print
              </Button>
            </Box>
          </Box>

          {/* Filter row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              mb: 2,
            }}
          >
            {/* Start date */}
            <Box sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <StartDatePicker
                fromDate={dateRangeFilter.fromDate}
                handleChangeFromDate={dateRangeFilter.handleChangeFromDate}
                toDate={dateRangeFilter.toDate}
                isCustomDateRange={true}
              />
            </Box>

            {/* End date */}
            <Box sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <EndDatePicker
                toDate={dateRangeFilter.toDate}
                handleChangeToDate={dateRangeFilter.handleChangeToDate}
                fromDate={dateRangeFilter.fromDate}
                isCustomDateRange={true}
              />
            </Box>

            {/* Incident Type */}
            <FormControl size="small" sx={{ minWidth: '120px', flex: '1 1 120px' }}>
              <Select
                displayEmpty
                value={getParam('type')}
                onChange={handleSelectFilter('type')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>Type</span>}
              >
                <MenuItem value="">All Types</MenuItem>
                {INCIDENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Status */}
            <FormControl size="small" sx={{ minWidth: '120px', flex: '1 1 120px' }}>
              <Select
                displayEmpty
                value={getParam('status')}
                onChange={handleSelectFilter('status')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>Call Status</span>}
              >
                <MenuItem value="">All</MenuItem>
                {callStatusOptions.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Local NERIS Status */}
            <FormControl size="small" sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <Select
                displayEmpty
                value={getParam('form_status')}
                onChange={handleSelectFilter('form_status')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>Local</span>}
              >
                <MenuItem value="">All Local</MenuItem>
                {LOCAL_NERIS_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            {/* USFA NERIS Status */}
            <FormControl size="small" sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <Select
                displayEmpty
                value={getParam('nerisStatus')}
                onChange={handleSelectFilter('nerisStatus')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>USFA</span>}
              >
                <MenuItem value="">All USFA</MenuItem>
                {USFA_NERIS_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Employee multi-select with search */}
            <FormControl size="small" sx={{ minWidth: '220px', flex: '1 1 220px' }}>
              <Select
                multiple
                displayEmpty
                value={getParam('employee') ? getParam('employee').split(',') : []}
                onChange={(e) => {
                  const value = e.target.value;
                  setParams((prev) => {
                    if (value.length > 0) {
                      prev.set('employee', value.join(','));
                    } else {
                      prev.delete('employee');
                    }
                    prev.set('page', '0');
                    return prev;
                  });
                }}
                onClose={() => setEmployeeSearch('')}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (selected.length === 0) return <span style={{ color: '#999' }}>Employee</span>;
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#bbb', borderRadius: 2 },
                      }}
                    >
                      {selected.map((id) => {
                        const emp = employeesList?.find((e) => String(e.id) === String(id));
                        return (
                          <Chip
                            key={id}
                            label={emp?.label || id}
                            size="small"
                            onDelete={(e) => {
                              e.stopPropagation();
                              const updated = selected.filter((s) => s !== id);
                              setParams((prev) => {
                                if (updated.length > 0) {
                                  prev.set('employee', updated.join(','));
                                } else {
                                  prev.delete('employee');
                                }
                                prev.set('page', '0');
                                return prev;
                              });
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{ height: 24, fontSize: '12px' }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
                sx={{
                  ...selectSx,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                  },
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 350 } },
                  MenuListProps: { sx: { pt: 0 } },
                  autoFocus: false,
                }}
              >
                <MenuItem
                  disableRipple
                  disableTouchRipple
                  dense
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ '&:hover': { backgroundColor: '#fff' }, '&.Mui-focusVisible': { backgroundColor: '#fff' }, cursor: 'default', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}
                >
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search..."
                    fullWidth
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </MenuItem>
                {(employeesList || [])
                  .filter((emp) => emp.label?.toLowerCase().includes(employeeSearch.toLowerCase()))
                  .map((emp) => (
                    <MenuItem key={emp.id} value={String(emp.id)}>
                      <Checkbox
                        size="small"
                        checked={getParam('employee').split(',').includes(String(emp.id))}
                      />
                      <ListItemText primary={emp.label} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          <Box>
            {loading || !employeesList ? (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            ) : !loading && incidentsList.length === 0 ? (
              <Typography className="mt-2" color="#6E7C87">
                No incidents found matching your search.
              </Typography>
            ) : (
              <IncidentsTable
                rows={rows}
                columns={columns}
                onPaginationModelChange={onPaginationModelChange}
                paginationModel={paginationModel}
                rowsCount={incidentsPagination?.total ?? 0}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* ── Print loading backdrop ── */}
      <Backdrop
        open={printLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <PrintIcon sx={{ fontSize: 48, opacity: 0.9 }} />
        <CircularProgress color="inherit" />
        <Typography
          sx={{ mt: 1, fontFamily: 'karla, sans-serif', fontSize: '1rem' }}
        >
          Loading report…
        </Typography>
      </Backdrop>

      {/* ── Print portal ── */}
      <IncidentPrintView incident={selectedIncidentInfo} department={department} />
      <IncidentsListPrintView
        rows={listPrintRows}
        department={department}
        filtersSummary={printFiltersSummary}
        callStatus={callStatus}
      />

      <ViewIncidentModal
        viewIncident={viewIncident}
        setViewIncident={setViewIncident}
      />
      {!!editIncident && (
        <EditIncidentModal
          editIncident={editIncident}
          setEditIncident={setEditIncident}
          onRevalidateData={requestForIncidents}
        />
      )}
      <CreateNote open={openCreateNoteModal} setOpen={setOpenCreateNoteModal} />
      <PropertyDamageModal
        open={openPropertyDamageModal}
        setOpen={setOpenPropertyDamageModal}
      />
    </>
  );
};

export default memo(Incidents);
